import { useCallback, useMemo } from 'react';

import { AppModel, useApp, useUpdateApp } from '../../../modules/Apps';
import { getApiErrorMessage } from '../../../utils/Api';
import { PopoverStateReturn } from '../../common/Drawer/Drawer.context';
import { FormDrawer } from '../../common/FormDrawer';
import { useToasterContext } from '../../common/Toaster';
import { ViewErrorState } from '../../common/ViewErrorState';
import { ViewLoadingState } from '../../common/ViewLoadingState';

import { useCreateNewTwilioAccount } from './lib';

import {
  AppModelToFormValues,
  AppSettingsForm,
  AppSettingsFormValues,
  AppSettingsFormValuesToDto,
  createAppSettingsFormValidationSchema,
  TwilioAccountOption,
} from '.';

interface CreateAppSettingsDrawerProps {
  state: PopoverStateReturn;
  app: AppModel;
}

export function EditAppSettingsDrawer({ state, app }: CreateAppSettingsDrawerProps) {
  const { mutateAsync } = useUpdateApp();
  const { showSuccess, showError } = useToasterContext();
  const createNewTwilioAccount = useCreateNewTwilioAccount();

  const handleSubmit = useCallback(
    async (values: AppSettingsFormValues) => {
      if (values.twilioAccountOption === TwilioAccountOption.New) {
        values.twilioAccountId = await createNewTwilioAccount(values);
      }
      await mutateAsync(AppSettingsFormValuesToDto(values), {
        onSuccess: () => showSuccess('App successfully updated.'),
        onError: error =>
          showError(
            getApiErrorMessage(
              error,
              'There was an error updating the App. Please, try again later.',
            ),
          ),
      });
    },
    [createNewTwilioAccount, mutateAsync, showError, showSuccess],
  );

  const { data, isLoading, isError } = useApp(app?.appId || '');

  const validationSchema = useMemo(() => createAppSettingsFormValidationSchema(), []);

  const initialValues = useMemo(() => (data ? AppModelToFormValues(data) : undefined), [data]);

  if (isError) {
    return <ViewErrorState />;
  }

  if (isLoading || !initialValues) {
    return <ViewLoadingState />;
  }

  return (
    <FormDrawer
      state={state}
      handleSubmit={handleSubmit}
      initialValues={initialValues}
      title="Edit Application"
      subtitle="Edit your existing OIM application settings"
      validationSchema={validationSchema}
      validateOnBlur={false}
      isError={isError}
      isLoading={isLoading}
      cancelLabel="Cancel"
      submitLabel="Save Changes"
    >
      {() => <AppSettingsForm isEditing />}
    </FormDrawer>
  );
}
